/* ui.tsx — the shared vocabulary of the Atlas screens.
 *
 * Deliberately small and deliberately NOT shadcn's defaults. Every one of these is a treatment the
 * visual spec names: hairline rules instead of cards, a 9px tracked label over a light figure, a
 * share bar only where the share is a real fraction, and the accent colour on data rather than on
 * chrome. `.atlas-flat` in tokens.css neutralises radius/shadow/gradient structurally, but a
 * component that has to be told not to look like a card is a component that will drift, so these
 * are built flat rather than corrected flat.
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { N, pct } from '../lib/fmt.ts';

export const HAIR = 'var(--hair)';

/* A rail section: a label on the shared left edge, a hairline underneath, nothing floating. */
export function Section({ label, right, children, style }: {
  label: string; right?: ReactNode; children: ReactNode; style?: CSSProperties;
}) {
  return (
    <div style={{ padding: 'var(--gut)', borderBottom: HAIR, ...style }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span className="t-label">{label}</span>
        {right != null && <span style={{ marginLeft: 'auto' }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

/* A collapsible rail group that STATES how many of its filters are live. That badge is the whole
   reason the sections are allowed to collapse at all — a collapsed section must never be able to
   hide a filter that is silently changing the count on screen. */
export function Collapsible({ label, live, open, onToggle, children }: {
  label: string; live: number; open: boolean; onToggle: () => void; children: ReactNode;
}) {
  return (
    <div style={{ borderBottom: HAIR }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
          padding: '10px var(--gut)', cursor: 'pointer', border: 'none', background: 'transparent',
          fontFamily: 'var(--sans)', fontSize: 'var(--t-label)', letterSpacing: '.13em',
          textTransform: 'uppercase', fontWeight: 600, color: open ? 'var(--dim)' : 'var(--dim2)',
        }}
      >
        <span style={{ display: 'inline-block', width: 8, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>▸</span>
        {label}
        <span className="num" style={{ marginLeft: 'auto', color: live ? 'var(--am)' : 'transparent', fontSize: 10 }}>
          {live || '0'}
        </span>
      </button>
      {open && <div style={{ padding: '0 var(--gut) var(--gut)' }}>{children}</div>}
    </div>
  );
}

export const Hint = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <div className="t-micro" style={{ lineHeight: 1.55, ...style }}>{children}</div>
);

/* Loud, never silent. A screen that quietly falls back to stale data is worse than one that says it
   is broken — so every fetch failure in this app renders this, with the message the server sent. */
export function ErrBox({ what, msg }: { what: string; msg: string }) {
  return (
    <div style={{ padding: 'var(--gut)', borderBottom: HAIR }}>
      <div className="t-label" style={{ color: 'var(--rd)' }}>{what}</div>
      <div style={{ marginTop: 6, color: 'var(--dim)', lineHeight: 1.6 }}>{msg}</div>
    </div>
  );
}

export const Loading = ({ what }: { what: string }) => (
  <div style={{ padding: 'var(--gut)', color: 'var(--dim)' }}>{what}</div>
);

/* A stat column, not a card. Rule-separated, --t-disp figure over a --t-label caption, and a share
   bar ONLY when `frac` is a real fraction of the population beside it. `dim` marks a figure that
   counts a DIFFERENT set from the one on screen — it gets no bar, because a bar would imply it
   belongs to this one. */
export function Stat({ label, value, sub, frac, dim, title }: {
  label: string; value: ReactNode; sub?: string; frac?: number | null; dim?: boolean; title?: string;
}) {
  return (
    <div title={title} style={{
      padding: 'var(--gut)', borderRight: HAIR, minWidth: 128, flex: '0 0 auto',
      opacity: dim ? 0.55 : 1,
    }}>
      <div className="t-label">{label}</div>
      <div className="t-disp" style={{ color: 'var(--ink)', marginTop: 2 }}>{value}</div>
      {sub && <div className="t-micro" style={{ color: 'var(--dim2)', marginTop: 3 }}>{sub}</div>}
      {frac != null && <Bar frac={frac} style={{ marginTop: 6 }} />}
    </div>
  );
}

export function Bar({ frac, style }: { frac: number; style?: CSSProperties }) {
  return (
    <div style={{ height: 3, background: 'var(--s2)', width: '100%', ...style }}>
      <div style={{ height: '100%', width: `${(100 * Math.max(0, Math.min(1, frac))).toFixed(1)}%`, background: 'var(--or)' }} />
    </div>
  );
}

/* A fact, not an action — so a chip is never orange unless it carries a figure worth acting on.
   `tone` maps to the three meanings the palette has: ok / warn / bad, plus the neutral default and
   `soft` for a value the data does not fully support. */
type Tone = 'none' | 'ok' | 'warn' | 'bad' | 'info' | 'soft';
const TONE: Record<Tone, string> = {
  none: 'var(--dim)', ok: 'var(--ok)', warn: 'var(--am)', bad: 'var(--rd)',
  info: 'var(--bl)', soft: 'var(--dim2)',
};
export function Chip({ tone = 'none', title, children }: { tone?: Tone; title?: string; children: ReactNode }) {
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 6px',
      border: HAIR, color: TONE[tone], fontSize: 'var(--t-micro)', whiteSpace: 'nowrap',
      fontStyle: tone === 'soft' ? 'italic' : undefined,
    }}>{children}</span>
  );
}

/* Tier is a FACT about the account, not an action, so it gets a neutral dot rather than the amber
   pill the buying role uses. Only T1 earns the space in a list. */
export const TierTag = ({ tier }: { tier?: string | null }) => (
  tier === '1'
    ? <span title="Tier 1 account" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--t-micro)', color: 'var(--dim)' }}>
      <i style={{ width: 5, height: 5, background: 'var(--am)', display: 'inline-block' }} />T1
    </span>
    : null
);

/* The committee meter. Four segments, not a coloured pill: every pill was the same shape, so
   coverage could only be read by parsing the digits on each row. This reads DOWN a column in one
   pass, which is the whole point of a worklist. */
export function Meter({ n, of = 4 }: { n: number; of?: number }) {
  return (
    <span title={`${n} of ${of} buying roles reachable in this building`} style={{ display: 'inline-flex', gap: 2 }}>
      {Array.from({ length: of }, (_, i) => (
        <i key={i} style={{
          width: 8, height: 3, display: 'inline-block',
          background: i < n ? (n === of ? 'var(--or)' : 'var(--am)') : 'var(--line2)',
        }} />
      ))}
    </span>
  );
}

/* A report table. Hairlines and a sticky header; figures right-aligned and mono so they line up on
   the digit down the column. */
export function RTable({ cols, children }: { cols: { k: string; w?: number; num?: boolean }[]; children: ReactNode }) {
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--t-body)' }}>
      <thead>
        <tr>
          {cols.map((c) => (
            <th key={c.k} style={{
              position: 'sticky', top: 0, zIndex: 1, background: 'var(--s3)', borderBottom: HAIR,
              padding: '7px 10px', width: c.w, textAlign: c.num ? 'right' : 'left',
              whiteSpace: 'nowrap', fontSize: 'var(--t-label)', letterSpacing: '.13em',
              textTransform: 'uppercase', fontWeight: 600, color: 'var(--dim2)',
            }}>{c.k}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

export const TD = ({ num, color, style, title, children }: {
  num?: boolean; color?: string; style?: CSSProperties; title?: string; children?: ReactNode;
}) => (
  <td className={num ? 'num' : undefined} title={title}
    style={{ padding: '5px 10px', whiteSpace: 'nowrap', color, ...style }}>{children}</td>
);

export const TR = ({ children, onClick, style }: { children: ReactNode; onClick?: () => void; style?: CSSProperties }) => (
  <tr onClick={onClick} style={{ borderBottom: HAIR, cursor: onClick ? 'pointer' : undefined, ...style }}>{children}</tr>
);

/* One click copies. A tel: link is useless without a softphone registered and a paste always works,
   so the big target is the copy and the dial is the small one beside it. */
export function Copy({ value, label, style }: { value: string; label?: string; style?: CSSProperties }) {
  const [done, setDone] = useState(false);
  return (
    <button
      title="click to copy"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value);
        setDone(true); setTimeout(() => setDone(false), 900);
      }}
      style={{
        border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', textAlign: 'left',
        fontFamily: 'var(--mono)', fontSize: 'var(--t-micro)', color: done ? 'var(--ok)' : 'var(--bl)',
        ...style,
      }}
    >{done ? 'copied' : (label ?? value)}</button>
  );
}

/* A flat button. `on` is a 2px accent rule + white text + a faint tint — never a solid orange
   block, which is criterion E1. */
export function Btn({ on, wide, sm, disabled, title, type, onClick, children, style }: {
  on?: boolean; wide?: boolean; sm?: boolean; disabled?: boolean; title?: string;
  type?: 'button' | 'submit'; onClick?: () => void; children: ReactNode; style?: CSSProperties;
}) {
  return (
    <button
      title={title} disabled={disabled} onClick={onClick} type={type || 'button'}
      style={{
        padding: sm ? '3px 7px' : '5px 9px', width: wide ? '100%' : undefined,
        cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left',
        border: HAIR, background: on ? 'rgba(255,170,0,.043)' : 'transparent',
        color: disabled ? 'var(--dim2)' : on ? '#FFF' : 'var(--dim)',
        /* A REAL BORDER, not an inset box-shadow. The Atlas reset in tokens.css carries
           `box-shadow: none !important` — criterion A2 is "zero box-shadows" — so the 2px accent
           rule that criterion B3 requires was being erased by the criterion next to it, on every
           active control in the product. Nothing looked broken; the active state just quietly
           became a tint and white text. Transparent when off so the control does not move 2px when
           it is selected. */
        borderBottom: on ? '2px solid var(--or)' : '2px solid transparent',
        fontSize: sm ? 'var(--t-micro)' : 'var(--t-body)', fontFamily: 'var(--sans)',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >{children}</button>
  );
}

/* A segmented control, from the same parts, so an active segment and an active tab look identical
   across the app. */
export function Seg<T extends string>({ value, options, onPick, wide }: {
  value: T; options: [T, string][]; onPick: (v: T) => void; wide?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {options.map(([id, label]) => (
        <Btn key={id} on={value === id} onClick={() => onPick(id)}
          style={{ flex: wide ? 1 : undefined, textAlign: 'center' }}>{label}</Btn>
      ))}
    </div>
  );
}

export const selStyle: CSSProperties = {
  width: '100%', padding: '4px 6px', border: HAIR, background: 'var(--s1)',
  color: 'var(--ink)', fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
};

/* The share-of-total bar used in the report tables, with its own number beside it so the bar is
   never the only way to read the value. */
export const ShareCell = ({ n, total }: { n: number; total: number }) => (
  <td style={{ padding: '5px 10px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <Bar frac={total ? n / total : 0} style={{ flex: '1 1 auto', minWidth: 40 }} />
      <span className="num t-micro" style={{ color: 'var(--dim2)', width: 34 }}>{pct(n, total)}</span>
    </div>
  </td>
);

export const Num = ({ v }: { v: unknown }) => <>{N(v)}</>;
